import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { dateTimeLabel, elapsedLabel } from '../../../whatsapp/clock';
import { reasonLabel } from '../../../whatsapp/inboxLogic';
import type { Conversation, ConversationDetail } from '../../../types/whatsapp';
import { CreatePatientDialog, LinkFailedDialog, LinkPatientDialog } from './PatientDialogs';

export interface ContextPanelProps {
  conversation: Conversation | null;
  detail: ConversationDetail | null;
  timeZone: string;
  /** Browser clock, for "hace N h". */
  nowMs: number;
}

const ICON_PROPS = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

const SparkIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z" />
  </svg>
);

const PersonIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const AlertIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);

const CARD = 'flex flex-col gap-3.5 rounded-[20px] border border-stone-200 bg-white p-5';
const EMPTY_REASON = 'Si la conversación pasó al equipo, aquí verás por qué.';

const CardHeader = ({ tile, icon, title, id }: { tile: string; icon: ReactNode; title: string; id: string }) => (
  <div className="flex items-center gap-2.5">
    <span className={`flex h-8 w-8 flex-none items-center justify-center rounded-[10px] ${tile}`}>{icon}</span>
    <h2 id={id} className="m-0 text-[15px] font-semibold text-stone-900">
      {title}
    </h2>
  </div>
);

const Rows = ({ children }: { children: ReactNode }) => <dl className="m-0 flex flex-col gap-2.5">{children}</dl>;

const Row = ({ label, value, mono = false }: { label: string; value: ReactNode; mono?: boolean }) => (
  <div className="flex items-start justify-between gap-4 text-[13px]">
    <dt className="flex-none text-stone-600">{label}</dt>
    <dd className={`m-0 min-w-0 text-right text-stone-800 ${mono ? 'font-geist-mono' : ''}`}>{value}</dd>
  </div>
);

const Body = ({ children }: { children: ReactNode }) => (
  <p className="m-0 text-[13px] leading-[1.45] text-stone-600">{children}</p>
);

const ReasonCard = ({ conversation, detail, timeZone, nowMs }: ContextPanelProps) => {
  const event = detail?.last_assignment_event ?? null;

  if (!conversation || !event) {
    return (
      <section className={CARD} aria-labelledby="ctx-motivo">
        <CardHeader id="ctx-motivo" tile="bg-stone-100 text-stone-700" icon={<SparkIcon />} title="Motivo del traspaso" />
        <Body>{EMPTY_REASON}</Body>
      </section>
    );
  }

  if (conversation.assigned_to === 'human-agent') {
    const urgent = event.reason === 'handoff_urgent';
    return (
      <section className={CARD} aria-labelledby="ctx-motivo">
        <CardHeader
          id="ctx-motivo"
          tile={urgent ? 'bg-red-100 text-red-700' : 'bg-stone-100 text-stone-700'}
          icon={<AlertIcon />}
          title="Por qué se pasó al equipo"
        />
        <Rows>
          <Row
            label="Motivo"
            value={
              <>
                {reasonLabel(event.reason)}
                {event.actor && <span className="block text-xs text-stone-600">Tomada por {event.actor.name}</span>}
              </>
            }
          />
          {event.topic_label !== null && <Row label="Tema" value={event.topic_label} />}
        </Rows>
      </section>
    );
  }

  const expired = event.reason === 'window_expired';
  // The window closes 24 h after the patient's last message.
  const closesAt = conversation.window.closes_at;
  const lastMessageAt = closesAt ? new Date(Date.parse(closesAt) - 24 * 3_600_000).toISOString() : null;
  const lastMessageAgo = elapsedLabel(lastMessageAt, nowMs);
  return (
    <section className={CARD} aria-labelledby="ctx-motivo">
      <CardHeader
        id="ctx-motivo"
        tile={expired ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-700'}
        icon={<PersonIcon />}
        title="Por qué volvió al asistente"
      />
      <Rows>
        {expired ? (
          <>
            <Row label="Motivo" value="Venció la ventana" />
            {lastMessageAgo && <Row label="Último mensaje" value={lastMessageAgo} mono />}
          </>
        ) : (
          <>
            <Row label="Motivo" value={event.actor ? `Devuelta por ${event.actor.name}` : reasonLabel(event.reason)} />
            <Row label="Cuándo" value={dateTimeLabel(event.created_at, timeZone)} mono />
            {event.actor && <Row label="Atendida por" value={`${event.actor.name} · Equipo`} />}
          </>
        )}
      </Rows>
    </section>
  );
};

type PatientDialog = 'link' | 'create' | 'failed';

const PatientCard = ({ conversation, detail, timeZone }: ContextPanelProps) => {
  const [dialog, setDialog] = useState<PatientDialog | null>(null);
  const [failedName, setFailedName] = useState('');
  const sectionRef = useRef<HTMLElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  // A conversation change closes whatever was open for the previous one.
  const conversationId = conversation?.id ?? null;
  useEffect(() => {
    setDialog(null);
  }, [conversationId]);

  // The dialog is gone: the keyboard lands on the new "Ver ficha", or back on the button that opened it.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (dialog === null && wasOpen.current) {
      const target =
        sectionRef.current?.querySelector<HTMLElement>('[data-after-link]') ??
        (openerRef.current?.isConnected ? openerRef.current : null);
      target?.focus();
    }
    wasOpen.current = dialog !== null;
  }, [dialog]);

  const openFrom = (kind: PatientDialog) => (event: React.MouseEvent<HTMLElement>) => {
    openerRef.current = event.currentTarget;
    setDialog(kind);
  };
  const close = () => setDialog(null);

  let content: ReactNode;

  if (!conversation) {
    content = <Body>Selecciona una conversación para ver la ficha del paciente.</Body>;
  } else if (!detail) {
    content = <Body>Cargando ficha…</Body>;
  } else if (detail.patient) {
    const { patient } = detail;
    content = (
      <>
        <Rows>
          <Row label="Nombre" value={patient.full_name} />
          <Row label="Teléfono" value={patient.phone} mono />
          <Row label="Edad" value={patient.age ?? '—'} />
          <Row
            label="Última visita"
            value={patient.last_visit_at ? dateTimeLabel(patient.last_visit_at, timeZone).slice(0, 10) : '—'}
            mono
          />
        </Rows>
        <Link
          to={`/history/${patient.id}`}
          data-after-link
          className="inline-flex h-10 items-center self-start rounded-full border border-blue-600 px-4 text-sm font-semibold text-blue-600 hover:bg-blue-50"
        >
          Ver ficha
        </Link>
      </>
    );
  } else {
    content = (
      <>
        <Body>Este número no está vinculado a ninguna ficha.</Body>
        <Rows>
          <Row label="Teléfono" value={conversation.display_phone} mono />
          {conversation.contact_name !== '' && <Row label="En WhatsApp" value={conversation.contact_name} />}
        </Rows>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={openFrom('create')}
            className="inline-flex h-10 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
          >
            Crear paciente
          </button>
          <button
            type="button"
            onClick={openFrom('link')}
            className="inline-flex h-10 items-center justify-center rounded-full border border-blue-600 bg-white text-sm font-semibold text-blue-600 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
          >
            Vincular a un paciente
          </button>
        </div>
      </>
    );
  }

  return (
    <section ref={sectionRef} className={CARD} aria-labelledby="ctx-paciente">
      <CardHeader id="ctx-paciente" tile="bg-blue-100 text-blue-700" icon={<PersonIcon />} title="Paciente" />
      {content}
      {conversation && dialog === 'link' && (
        <LinkPatientDialog
          key={conversation.id}
          conversation={conversation}
          onClose={close}
          onLinked={close}
          onCreate={() => setDialog('create')}
        />
      )}
      {conversation && dialog === 'create' && (
        <CreatePatientDialog
          key={conversation.id}
          conversation={conversation}
          onClose={close}
          onLinked={close}
          onLinkFailed={(name) => {
            setFailedName(name);
            setDialog('failed');
          }}
        />
      )}
      {dialog === 'failed' && <LinkFailedDialog name={failedName} onClose={close} />}
    </section>
  );
};

export const ContextPanel = (props: ContextPanelProps) => (
  <div className="flex flex-col gap-5">
    <ReasonCard {...props} />
    <PatientCard {...props} />
  </div>
);
